import React, { FC, useContext, useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  GameServiceContext,
  LoggerServiceContext,
  I18nServiceContext,
} from "../../services";
import { TransTokens } from "../../i18n";
import { GameReleaseModel } from "../../models";
import { useLocation } from "react-router-dom";

export const ensureGameFileTokens = TransTokens.create({
  failed:
    "Could not load the necessary files to start this story. Please check your connection and tap to try again.",
});

export interface EnsureGameFileProps {
  release: GameReleaseModel;
  renderError?: (retry: () => void, error: Error) => React.ReactNode;
  renderLoading?: () => React.ReactNode;
  enforceDefaultLanguage?: boolean;
  children:
    | ((
        bundleFile: string,
        setError: (error: Error) => void
      ) => React.ReactNode)
    | React.ReactNode;
  lazy?: boolean;
}

export const EnsureGameFile: FC<EnsureGameFileProps> = observer(
  ({ release, renderError, children, lazy, renderLoading }) => {
    const [ready, setReady] = useState<string>();
    const [error, setError] = useState<Error>();

    const { search } = useLocation();

    const query = new URLSearchParams(search);
    const requestLang = query.get("lang");

    const [promise, setPromise] = useState<Promise<any>>(
      new Promise(() => null)
    );

    const gameService = useContext(GameServiceContext);
    const logger = useContext(LoggerServiceContext);

    const { savedPreferredLanguage } = useContext(I18nServiceContext);

    const bundleFile = gameService.resolveBundleFile(
      release,
      requestLang || savedPreferredLanguage
    );

    function refresh() {
      setError(undefined);

      setReady(undefined);

      const bundle = bundleFile;
      setPromise(
        gameService
          .downloadGameFile(release, bundleFile)
          .then(() => {
            if (bundle === bundleFile) setReady(bundleFile);
          })
          .catch((e) => {
            logger.capture(e);

            if (bundle === bundleFile) setError(e);
          })
      );
    }

    function onChildError(error: Error) {
      setError(error);
      setReady(undefined);
    }

    useEffect(refresh, [bundleFile]);

    if (ready) {
      return (
        <>
          {typeof children === "function"
            ? children(bundleFile, onChildError)
            : children}
        </>
      );
    }

    if (lazy) {
      return <LoableProgress promise={promise} error={error} />;
    }

    if (error) {
      return <>{renderError?.(refresh, error)}</>;
    }

    return <>{renderLoading?.()}</>;
  }
);

const LoableProgress: FC<{ promise: Promise<any>; error?: Error }> = ({
  promise,
  error,
}) => {
  if (error) throw error;

  throw promise;
};
